import * as Sentry from '@sentry/react';
import { createFileRoute, useLocation, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { queryKeys, useGetImageQuery } from '@vizit/api';
import { AssetDetailsTabOptions } from '@vizit/assets';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { buildDynamicBreadcrumbs } from '@vizit/utils';
import * as React from 'react';
import { AssetDetailsPage, PagesProvider } from '../../../pages';
import { LIBRARY_BREADCRUMB, beforeLoadAuthenticatedValidation } from '../../../router';
import { BreadcrumbsPageTemplate } from '../../../templates';

export const Route = createFileRoute('/_authenticated/library/assets/$assetId')({
  staticData: {
    pageName: 'Asset Details',
  },
  beforeLoad: ({ context, location }) => beforeLoadAuthenticatedValidation(context, location),
  component: RouteComponent,
  loader: async ({ location, params, context }) => {
    const organizationId = context.auth?.organizationId;
    const portfolioId = context.auth?.defaultPortfolioId;

    let assetName = 'Asset Details';

    if (organizationId && portfolioId) {
      try {
        const imageData = await context.queryClient.ensureQueryData({
          queryKey: queryKeys.images.detail({
            organization_id: organizationId,
            portfolio_id: portfolioId,
            image_id: params.assetId,
          }).queryKey,
          queryFn: () =>
            useGetImageQuery.fetcher({
              organization_id: organizationId,
              portfolio_id: portfolioId,
              image_id: params.assetId,
            })(),
        });

        assetName = imageData?.name ?? 'Asset Details';
      } catch (error) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Navigation,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Navigation].breadcrumbs,
          },
          extra: {
            page: 'asset details',
          },
        });
      }
    }

    const breadcrumbs = buildDynamicBreadcrumbs(
      location.state?.from,
      assetName,
      // Current page is not clickable, so we don't need to pass href
      undefined,
      location.search,
      LIBRARY_BREADCRUMB,
    );

    return { breadcrumbs };
  },
  validateSearch: (search: Record<string, unknown>): { tab: AssetDetailsTabOptions } => {
    const tab =
      typeof search.tab === 'string' &&
      Object.values(AssetDetailsTabOptions as Record<string, unknown>).includes(search.tab)
        ? (search.tab as AssetDetailsTabOptions)
        : AssetDetailsTabOptions.ImageAnalysis;
    return { tab };
  },
});

function RouteComponent() {
  const router = useRouter();
  const search = useSearch({ from: Route.id });
  const location = useLocation();
  const { assetId } = useParams({ from: Route.id });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Need to memoize once on render, otherwise it will change.
  const previousPageState = React.useMemo(() => location.state, []);

  const handleOnTabChange = React.useCallback(
    async (key: AssetDetailsTabOptions) => {
      await router.navigate({
        to: Route.fullPath,
        params: { assetId },
        search: (prev: Record<string, unknown>) => ({ ...prev, tab: key }),
        state: previousPageState,
      });
    },
    [router, assetId, previousPageState],
  );

  return (
    <PagesProvider template={BreadcrumbsPageTemplate} templateProps={{ className: 'h-screen' }}>
      <AssetDetailsPage assetId={assetId} selectedTab={search.tab} onTabChange={handleOnTabChange} />
    </PagesProvider>
  );
}
